import { convexQuery } from "@convex-dev/react-query";
import { formatCentsAsCurrency } from "@plasma/utils/money-formatters";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useAction } from "convex/react";
import { AlertTriangle, Package } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  hardwareOrdersCreateOrder,
  hardwareOrdersGetAvailableSKUs,
  hardwareOrdersGetShippingMethods,
  hardwareOrdersPreviewOrder,
  locationsGetById,
} from "~/api-refs";
import type {
  HardwareSKU,
  OrderPreview,
  ShippingMethod,
} from "~/components/settings/terminal-order-steps";
import {
  OrderPreviewStep,
  OrderSuccessStep,
  SelectReaderStep,
} from "~/components/settings/terminal-order-steps";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Spinner } from "~/components/ui/spinner";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";

type Step = "select" | "preview" | "success";

const ORDER_MODAL_COPY: Record<
  Step,
  { title: string; description: (locationName?: string) => string }
> = {
  select: {
    title: "Order Card Reader",
    description: (locationName?: string) =>
      `Order a card reader for ${locationName ?? "your location"}`,
  },
  preview: {
    title: "Review Order",
    description: () => "Review your card reader order before placing it.",
  },
  success: {
    title: "Order Confirmed",
    description: () => "Your card reader order has been placed successfully.",
  },
};

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/settings/terminals/order/$locationId"
)({
  component: OrderReaderModal,
});

function OrderReaderModal() {
  const navigate = useNavigate();
  const { locationId } = useParams({
    from: "/_authenticated/settings/terminals/order/$locationId",
  });

  const { data: location } = useQuery(
    convexQuery(locationsGetById, {
      id: toConvexTableId("locations", locationId),
    })
  );

  const getAvailableSKUs = useAction(hardwareOrdersGetAvailableSKUs);
  const getShippingMethods = useAction(hardwareOrdersGetShippingMethods);
  const previewOrder = useAction(hardwareOrdersPreviewOrder);
  const createOrder = useAction(hardwareOrdersCreateOrder);

  // Step tracking
  const [step, setStep] = useState<Step>("select");

  // Data loading
  const [skus, setSkus] = useState<HardwareSKU[]>([]);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Form state
  const [selectedSku, setSelectedSku] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedShipping, setSelectedShipping] = useState<string>("");

  // Preview state
  const [preview, setPreview] = useState<OrderPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Order state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState<{
    orderId?: string;
    stripeOrderId?: string;
  } | null>(null);

  const handleClose = () => {
    void navigate({ to: "/settings/terminals" });
  };

  // Load SKUs and shipping methods
  const loadData = useCallback(async () => {
    setIsLoadingData(true);
    setLoadError(null);

    try {
      const [skusData, shippingData] = await Promise.all([
        getAvailableSKUs({ country: "US" }),
        getShippingMethods({ country: "US" }),
      ]);
      setSkus(skusData);
      setShippingMethods(shippingData);

      // Pre-select defaults if available
      const firstSku = skusData[0];
      const firstShipping = shippingData[0];
      if (firstSku && !selectedSku) {
        setSelectedSku(firstSku.id);
      }
      if (firstShipping && !selectedShipping) {
        setSelectedShipping(firstShipping.id);
      }
    } catch (error) {
      logger.exception("terminal_order_data_load_failed", error);
      setLoadError(
        error instanceof Error
          ? error.message
          : "Failed to load available card readers"
      );
    } finally {
      setIsLoadingData(false);
    }
  }, [getAvailableSKUs, getShippingMethods, selectedSku, selectedShipping]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Load preview
  const handlePreviewOrder = async () => {
    if (!(selectedSku && selectedShipping)) {
      toast.error("Please select a card reader and shipping method");
      return;
    }

    setIsLoadingPreview(true);
    try {
      const previewData = await previewOrder({
        locationId: toConvexTableId("locations", locationId),
        items: [{ sku: selectedSku, quantity }],
        shippingMethodId: selectedShipping,
      });
      setPreview(previewData);
      setStep("preview");
    } catch (error) {
      logger.exception("terminal_order_preview_failed", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to preview order"
      );
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Submit order
  const handleSubmitOrder = async () => {
    if (!preview) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createOrder({
        locationId: toConvexTableId("locations", locationId),
        items: [{ sku: selectedSku, quantity }],
        shippingMethodId: selectedShipping,
      });

      if (result.success) {
        setOrderResult({
          orderId: result.orderId?.toString(),
          stripeOrderId: result.stripeOrderId,
        });
        setStep("success");
        toast.success("Card reader order placed successfully");
      } else {
        toast.error(result.error ?? "Order failed");
      }
    } catch (error) {
      logger.exception("terminal_order_create_failed", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to place order"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalCopy = ORDER_MODAL_COPY[step];

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="size-5" />
            {modalCopy.title}
          </DialogTitle>
          <DialogDescription>
            {modalCopy.description(location?.name)}
          </DialogDescription>
        </DialogHeader>

        {/* Loading State */}
        {isLoadingData && (
          <div className="flex flex-col items-center justify-center py-8">
            <Spinner className="size-8" />
            <p className="text-muted-foreground mt-2 text-sm">
              Loading available card readers...
            </p>
          </div>
        )}

        {/* Error State */}
        {loadError && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertTriangle className="text-destructive mb-2 size-8" />
            <p className="text-destructive text-sm font-medium">{loadError}</p>
            <Button className="mt-4" onClick={loadData} variant="outline">
              Try Again
            </Button>
          </div>
        )}

        {/* Step 1: Select Reader and Options */}
        {!(isLoadingData || loadError) && step === "select" && (
          <SelectReaderStep
            location={location}
            quantity={quantity}
            selectedShipping={selectedShipping}
            selectedSku={selectedSku}
            setQuantity={setQuantity}
            setSelectedShipping={setSelectedShipping}
            setSelectedSku={setSelectedSku}
            shippingMethods={shippingMethods}
            skus={skus}
          />
        )}

        {/* Step 2: Order Preview */}
        {step === "preview" && preview && (
          <OrderPreviewStep preview={preview} />
        )}

        {/* Step 3: Success */}
        {step === "success" && <OrderSuccessStep orderResult={orderResult} />}

        <DialogFooter>
          {step === "select" && (
            <>
              <Button onClick={handleClose} type="button" variant="outline">
                Cancel
              </Button>
              <Button
                disabled={
                  !(selectedSku && selectedShipping) || isLoadingPreview
                }
                onClick={handlePreviewOrder}
              >
                {isLoadingPreview ? (
                  <>
                    <Spinner className="mr-2 size-4" />
                    Preparing order...
                  </>
                ) : (
                  "Review Order"
                )}
              </Button>
            </>
          )}

          {step === "preview" && (
            <>
              <Button
                onClick={() => setStep("select")}
                type="button"
                variant="outline"
              >
                Back
              </Button>
              <Button disabled={isSubmitting} onClick={handleSubmitOrder}>
                {isSubmitting ? (
                  <>
                    <Spinner className="mr-2 size-4" />
                    Placing Order...
                  </>
                ) : (
                  `Pay ${preview ? formatCentsAsCurrency(preview.totalAmount) : ""}`
                )}
              </Button>
            </>
          )}

          {step === "success" && (
            <Button className="w-full" onClick={handleClose}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
